import { fetchAppointmentById } from "../../services/fetchAppointmentById";
import { applyTeleconsultoriaInvite } from "../../services/applyTeleconsultoriaInvite";
import {
  inviteAppointmentId,
  inviteGuestFromTask,
  inviteLocationReference,
  isInvitePending,
} from "../helpers/inviteTaskHelpers";
import { isSamePractitionerReference } from "../../utils/actors";
import { InviteActionError } from "./inviteActionError";
import { notifyInviteResponse, patchInviteStatus, readInviteTask } from "./inviteTaskService";

const CLOSED_APPOINTMENT_STATUSES = new Set(["cancelled", "noshow", "entered-in-error", "fulfilled"]);

/**
 * Lê o Appointment de origem. Sem ele não dá para entrar no atendimento.
 * Filtra pela Location de onde veio o convite (`Task.location`): o scheduling
 * exige esse filtro e o convidado ainda não está no Appointment.
 */
async function fetchOriginAppointment(task: fhir5.Task): Promise<fhir5.Appointment> {
  try {
    // Busca por `_id` (padrão do sistema); a leitura `/Appointment/<id>` dava 500.
    const appointment = await fetchAppointmentById(
      inviteAppointmentId(task),
      inviteLocationReference(task),
    );
    if (appointment) return appointment;
  } catch (error) {
    console.error("respondTeleconsultoriaInvite.fetchOriginAppointment", error);
  }
  throw new InviteActionError("Não foi possível abrir o atendimento de origem.");
}

/**
 * Convidado aceita e entra.
 * 1. Valida o convite (dele, pendente ou já aceito) e o Appointment de origem.
 * 2. Reutiliza `applyTeleconsultoriaInvite` (PATCH do participant CON + espelho).
 *    Ele recusa se já houver OUTRO teleconsultor e não duplica o mesmo.
 * 3. Marca a Task como aceita.
 * Idempotente: repetir depois de uma falha (ou "entrar de novo") não duplica nada.
 */
export async function acceptTeleconsultoriaInvite(params: {
  task: fhir5.Task;
  practitionerReference: string;
}): Promise<fhir5.Appointment> {
  if (!params.task.id) throw new InviteActionError("Este convite não está mais disponível.");
  const { practitionerReference } = params;
  // Relê a Task: o host pode ter cancelado depois que a notificação chegou.
  const task = await readInviteTask(params.task.id);
  const isAccepted = task.status === "accepted";
  if (!task.id || (!isAccepted && !isInvitePending(task))) {
    throw new InviteActionError("Este convite não está mais disponível.");
  }

  const guest = inviteGuestFromTask(task);
  if (!guest || !isSamePractitionerReference(guest.practitioner.reference, practitionerReference)) {
    throw new InviteActionError("Este convite é para outro profissional.");
  }

  const appointment = await fetchOriginAppointment(task);
  if (CLOSED_APPOINTMENT_STATUSES.has(appointment.status || "")) {
    throw new InviteActionError("O atendimento de origem já foi encerrado ou cancelado.");
  }

  const updated = await applyTeleconsultoriaInvite({ appointment, guest });
  if (!isAccepted) {
    await patchInviteStatus(task.id, "accepted");
    // Avisa quem convidou (só na 1ª vez; "entrar de novo" não notifica de novo).
    await notifyInviteResponse({
      inviteTask: task,
      response: "accepted",
      appointmentStart: appointment.start,
    });
  }
  return updated;
}

/** Convidado recusa (relendo a Task, pelo mesmo motivo do aceite). */
export async function rejectTeleconsultoriaInvite(task: fhir5.Task): Promise<void> {
  if (!task.id) throw new InviteActionError("Este convite não está mais disponível.");
  const current = await readInviteTask(task.id);
  if (!isInvitePending(current)) {
    throw new InviteActionError("Este convite não está mais disponível.");
  }
  await patchInviteStatus(task.id, "rejected");
  // Avisa quem convidou que o convite foi recusado.
  await notifyInviteResponse({ inviteTask: current, response: "rejected" });
}

/**
 * Host cancela o convite que ainda não foi respondido.
 * Relê a Task antes: a tela do host não sabe se o convidado acabou de aceitar.
 */
export async function cancelTeleconsultoriaInvite(task: fhir5.Task): Promise<void> {
  if (!task.id) throw new InviteActionError("Este convite já foi respondido.");
  const current = await readInviteTask(task.id);
  if (current.status !== "requested") {
    throw new InviteActionError("Este convite já foi respondido.");
  }
  await patchInviteStatus(task.id, "cancelled");
}
